import { useState, useRef } from "react";
import { Autocomplete } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { searchCompanies } from "../api";

/**
 * Company name input with autocomplete from the global company database.
 * When a known company is selected, calls onChange with { name, id }.
 * When free text is typed, calls onChange with { name, id: null }.
 */
export default function CompanyAutocomplete({ value, onChange, size, ...rest }) {
  const { t } = useTranslation("Veft");
  const [options, setOptions] = useState([]);
  const [companyMap, setCompanyMap] = useState({});
  const debounceRef = useRef(null);

  function handleChange(val) {
    const matched = companyMap[val];
    onChange({ name: val, id: matched ?? null });

    // Debounced search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await searchCompanies(val.trim());
        const map = {};
        const names = results.map((c) => {
          map[c.name] = c.id;
          return c.name;
        });
        setCompanyMap(map);
        setOptions(names);
      } catch {
        setOptions([]);
      }
    }, 250);
  }

  function handleOptionSubmit(val) {
    const matched = companyMap[val];
    onChange({ name: val, id: matched ?? null });
  }

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      onOptionSubmit={handleOptionSubmit}
      data={options}
      placeholder={t("company_placeholder")}
      size={size}
      {...rest}
    />
  );
}
